import React, { useState, useEffect, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  THEME_COLOR,
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";
import api from "../../../../utils/axios";
import { getAlert, parseCurrency } from "../../../../utils/SamuiUtils";
import { InitialDataTeam } from "../../../../model/InitialData/InitialDataTeam";

const generateNumericPassword = () =>
  Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join("");

const THAI_MOBILE_PATTERN = /^08\d-\d{3}-\d{4}$/;

const formatThaiMobileNumber = (value) => {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const TeamForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
  dataEmployees,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [showMemberPasswords, setShowMemberPasswords] = useState({});
  const colorInputRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const handleBack = () => {
    window.location.href = "/team";
  };

  useEffect(() => {
    const compId = localStorage.getItem("company");
    if (compId) {
      setFormData((prevData) => ({
        ...prevData,
        comp_id: compId,
      }));
    }
  }, [setFormData]);

  useEffect(() => {
    if (mode === "I") {
      const newPassword = generateNumericPassword();
      setGeneratedPassword(newPassword);
      const initialData = {
        team_id: null,
        comp_id: parseCurrency(window.localStorage.getItem("company"), 10),
        team_name: "",
        team_leader_id: "",
        team_color: "",
        vehicle_no: "",
        team_phone1: "",
        team_phone2: "",
        team_phone3: "",
        team_status: "A",
        team_username: "",
        team_password: newPassword,
        team_pin: "",
        auto_create_wh: true, // ค่าเริ่มต้น: ติ๊กไว้
        team_members: [],
      };
      setFormData(initialData);
    }
  }, [mode, setFormData]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            handleBack();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [formData]);

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleShowMemberPassword = (index) => {
    setShowMemberPasswords((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (event, phoneField) => {
    const formattedValue = formatThaiMobileNumber(event.target.value);
    setFormData((prev) => ({
      ...prev,
      [phoneField]: formattedValue,
    }));
  };

  const handleMemberPhoneChange = (index, value) => {
    const formattedValue = formatThaiMobileNumber(value);
    handleMemberChange(index, "member_phone", formattedValue);
  };

  const handleColorChange = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      team_color: value,
    }));
  };

  const handleAddMember = () => {
    setFormData((prev) => ({
      ...prev,
      team_members: [
        ...(prev.team_members || []),
        { 
          member_name: "", 
          member_phone: "",
          member_username: "",
          member_password: "",
          member_pin: "",
        },
      ],
    }));
  };

  const handleRemoveMember = (index) => {
    setFormData((prev) => ({
      ...prev,
      team_members: prev.team_members.filter((_, i) => i !== index),
    }));
  };

  const handleMemberChange = (index, field, value) => {
    setFormData((prev) => {
      const newMembers = [...(prev.team_members || [])];
      newMembers[index] = {
        ...newMembers[index],
        [field]: value,
      };
      return {
        ...prev,
        team_members: newMembers,
      };
    });
  };

  const resetForm = () => {
    setFormData(InitialDataTeam());
    setGeneratedPassword("");
  };

  const handleSubmit = async () => {
    if (isSavingRef.current) return;
    try {
      if (!formData.team_name) {
        getAlert("WARNING", "โปรดกรอกชื่อทีม");
        return;
      }

      if (!formData.team_username || !formData.team_password) {
        getAlert("WARNING", "โปรดกรอกข้อมูลบัญชีผู้ใช้งาน (Username และ Password)");
        return;
      }

      const compId = parseCurrency(window.localStorage.getItem("company"), 10);
      const empId = parseCurrency(window.localStorage.getItem("emp_id"), 10);
      const empName = window.localStorage.getItem("name") || "";

      // ลบ dash ออกจากเบอร์โทรก่อนส่งไป backend
      const cleanPhone = (phone) => {
        if (!phone) return null;
        return phone.replace(/-/g, "").trim() || null;
      };

      const payload = {
        Comp_Id: compId,
        Team_Name: formData.team_name.trim(),
        Team_Leader_Id: formData.team_leader_id
          ? parseCurrency(formData.team_leader_id, 10)
          : null,
        Team_Color: formData.team_color?.trim() || null,
        Vehicle_No: formData.vehicle_no?.trim() || null,
        Team_Phone1: cleanPhone(formData.team_phone1),
        Team_Phone2: cleanPhone(formData.team_phone2),
        Team_Phone3: cleanPhone(formData.team_phone3),
        Team_Status: formData.team_status || "A",
        Team_Username: formData.team_username?.trim() || null,
        Team_Password: formData.team_password?.trim() || null,
        Team_Pin: formData.team_pin?.trim() || null,
        Auto_Create_WH: formData.auto_create_wh || false,
        Created_By_Id: empId,
        Created_By_Name: empName,
        Updated_By_Id: empId,
        Updated_By_Name: empName,
        Team_Members: (formData.team_members || []).filter(
          (m) => m.member_name && m.member_name.trim()
        ).map((m) => ({
          Member_Name: m.member_name.trim(),
          Member_Phone: cleanPhone(m.member_phone),
          Member_Username: m.member_username?.trim() || null,
          Member_Password: m.member_password?.trim() || null,
          Member_Pin: m.member_pin?.trim() || null,
        })),
      };

      if (mode === "I") {
        isSavingRef.current = true;
        setIsSaving(true);
        const res = await api.post("/create-team", payload);
        if (res.data?.status === "OK") {
          getAlert("SUCCESS", "บันทึกข้อมูลสำเร็จ");
          await callInitialize();
          resetForm();
        } else {
          getAlert("FAILED", res.data?.message || "ไม่สามารถบันทึกข้อมูลได้");
        }
      } else if (mode === "U") {
        const res = await api.put(`/update-team/${formData.team_id}`, payload);
        if (res.data?.status === "OK") {
          getAlert("SUCCESS", "แก้ไขข้อมูลสำเร็จ");
          await callInitialize();
          resetForm();
        } else {
          getAlert("FAILED", res.data?.message || "ไม่สามารถแก้ไขข้อมูลได้");
        }
      }
    } catch (error) {
      console.error("Error submitting team:", error);
      getAlert(
        "FAILED",
        error.response?.data?.message || "เกิดข้อผิดพลาดในการบันทึกข้อมูล"
      );
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
        if (isConfirm && selectedData?.Team_Id) {
          try {
            const res = await api.delete(`/delete-team/${selectedData.Team_Id}`);
            if (res.data?.status === "OK") {
              getAlert("SUCCESS", "ลบข้อมูลสำเร็จ");
              await callInitialize();
            } else {
              getAlert("FAILED", res.data?.message || "ไม่สามารถลบข้อมูลได้");
            }
          } catch (error) {
            console.error("Error deleting team:", error);
            getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
          }
        }
      });
    } catch (error) {
      getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  return (
    <InitialDataFormLayout
      name={name}
      entityPath="/team"
      mode={mode}
      onSubmit={handleSubmit}
      isSubmittingDisabled={isSaving}
      onBack={handleBack}
      onReset={resetForm}
      onDelete={mode === "U" ? handleDelete : undefined}
      callInitialize={callInitialize}
    >
      <div className="mb-5">
        <div style={sectionHeaderStyle}>
          <i className="fas fa-users" style={sectionIconStyle}></i>
          <h5 style={sectionTitleStyle}>ข้อมูลทีม</h5>
        </div>
        <div className="row g-4">
          <div className="col-md-4">
            <label style={labelStyle}>ชื่อทีม *</label>
            <input
              type="text"
              className="form-control"
              name="team_name"
              value={formData.team_name || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div className="col-md-4">
            <label style={labelStyle}>หัวหน้าทีม</label>
            <select
              className="form-select"
              name="team_leader_id"
              value={formData.team_leader_id || ""}
              onChange={handleChange}
              style={selectStyle}
            >
              <option value="">— ไม่ระบุ (บัญชีด้านล่างเก็บที่ข้อมูลทีมเท่านั้น) —</option>
              {(dataEmployees || []).map((emp) => (
                <option key={emp.Emp_Id} value={String(emp.Emp_Id)}>
                  {emp.Emp_Name || emp.Emp_Code || emp.Emp_Id}
                  {emp.Emp_Code ? ` (${emp.Emp_Code})` : ""}
                </option>
              ))}
            </select>
            <small className="text-muted d-block mt-1">
              ถ้าเลือกหัวหน้า ระบบจะบันทึก Username / Password / PIN ไปที่พนักงานคนนี้ด้วย (สำหรับล็อกอิน)
            </small>
          </div>
          <div className="col-md-4">
            <label style={labelStyle}>สีทีม</label>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                ref={colorInputRef}
                type="color"
                value={formData.team_color || "#000000"}
                onChange={handleColorChange}
                style={{
                  position: "absolute",
                  width: "0",
                  height: "0",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
              <div
                onClick={() => colorInputRef.current?.click()}
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "8px",
                  backgroundColor: formData.team_color || "#000000",
                  border: "2px solid #ced4da",
                  flexShrink: 0,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                  cursor: "pointer",
                  transition: "transform 0.1s ease, box-shadow 0.1s ease",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.15)";
                }}
                title="คลิกเพื่อเลือกสี"
              />
              <input
                type="text"
                className="form-control"
                placeholder="#000000 หรือชื่อสี"
                value={formData.team_color || ""}
                onChange={handleChange}
                name="team_color"
                style={inputStyle}
              />
            </div>
          </div>
          <div className="col-md-4">
            <label style={labelStyle}>ทะเบียนรถ</label>
            <input
              type="text"
              className="form-control"
              name="vehicle_no"
              value={formData.vehicle_no || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div className="col-md-4">
            <label style={labelStyle}>เบอร์โทร 1</label>
            <input
              type="tel"
              className="form-control"
              name="team_phone1"
              value={formData.team_phone1 || ""}
              onChange={(e) => handlePhoneChange(e, "team_phone1")}
              style={inputStyle}
              placeholder="08x-xxx-xxxx"
              inputMode="tel"
              pattern="^08\\d-\\d{3}-\\d{4}$"
              maxLength={12}
            />
          </div>
          <div className="col-md-4">
            <label style={labelStyle}>เบอร์โทร 2</label>
            <input
              type="tel"
              className="form-control"
              name="team_phone2"
              value={formData.team_phone2 || ""}
              onChange={(e) => handlePhoneChange(e, "team_phone2")}
              style={inputStyle}
              placeholder="08x-xxx-xxxx"
              inputMode="tel"
              pattern="^08\\d-\\d{3}-\\d{4}$"
              maxLength={12}
            />
          </div>
          <div className="col-md-4">
            <label style={labelStyle}>เบอร์โทร 3</label>
            <input
              type="tel"
              className="form-control"
              name="team_phone3"
              value={formData.team_phone3 || ""}
              onChange={(e) => handlePhoneChange(e, "team_phone3")}
              style={inputStyle}
              placeholder="08x-xxx-xxxx"
              inputMode="tel"
              pattern="^08\\d-\\d{3}-\\d{4}$"
              maxLength={12}
            />
          </div>
          <div className="col-md-2">
            <label style={labelStyle}>สถานะ</label>
            <select
              className="form-select"
              name="team_status"
              value={formData.team_status || "A"}
              onChange={handleChange}
              style={selectStyle}
            >
              <option value="A">Active (ใช้งาน)</option>
              <option value="I">Inactive (ไม่ใช้งาน)</option>
            </select>
          </div>
          <div className="col-md-10 d-flex align-items-center" style={{ paddingTop: '32px' }}>
            <label style={labelStyle} className="d-flex align-items-center gap-2 mb-0">
              <input
                type="checkbox"
                name="auto_create_wh"
                checked={formData.auto_create_wh || false}
                onChange={(e) => setFormData({ ...formData, auto_create_wh: e.target.checked })}
                className="form-check-input"
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span>สร้างคลังอัตโนมัติ</span>
            </label>
          </div>
        </div>
      </div>

      <div className="mb-5">
        <div style={sectionHeaderStyle}>
          <i className="fas fa-lock" style={sectionIconStyle}></i>
          <h5 style={sectionTitleStyle}>บัญชีผู้ใช้งาน</h5>
        </div>
        <div className="row g-4">
          <div className="col-md-4">
            <label style={labelStyle}>Username *</label>
            <input
              type="text"
              className="form-control"
              name="team_username"
              value={formData.team_username || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div className="col-md-4">
            <label style={labelStyle}>Password *</label>
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                name="team_password"
                value={formData.team_password || ""}
                onChange={handleChange}
                style={inputStyle}
              />
              <button
                type="button"
                className="btn text-white"
                onClick={toggleShowPassword}
                style={{
                  backgroundColor: THEME_COLOR,
                  borderRadius: "0 8px 8px 0",
                }}
              >
                <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
              </button>
            </div>
          </div>
          <div className="col-md-4">
            <label style={labelStyle}>PIN</label>
            <input
              type="text"
              className="form-control"
              name="team_pin"
              value={formData.team_pin || ""}
              onChange={handleChange}
              style={inputStyle}
              placeholder="รหัส PIN สำหรับทีม"
            />
          </div>
        </div>
      </div>

      <div className="mb-5">
        <div style={sectionHeaderStyle}>
          <i className="fas fa-user-friends" style={sectionIconStyle}></i>
          <h5 style={sectionTitleStyle}>ลูกทีม</h5>
        </div>
        <div className="row g-4">
          <div className="col-12">
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleAddMember}
              style={{
                backgroundColor: THEME_COLOR,
                color: "white",
                marginBottom: "16px",
              }}
            >
              <i className="fas fa-plus" /> เพิ่มลูกทีม
            </button>
          </div>
          {(formData.team_members || []).map((member, index) => (
            <div key={index} className="col-12">
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "16px",
                  backgroundColor: "#f9fafb",
                }}
              >
                <div className="row g-3">
                  <div className="col-md-3">
                    <label style={labelStyle}>ชื่อ</label>
                    <input
                      type="text"
                      className="form-control"
                      value={member.member_name || ""}
                      onChange={(e) =>
                        handleMemberChange(index, "member_name", e.target.value)
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div className="col-md-2">
                    <label style={labelStyle}>เบอร์โทร</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={member.member_phone || ""}
                      onChange={(e) =>
                        handleMemberPhoneChange(index, e.target.value)
                      }
                      style={inputStyle}
                      placeholder="08x-xxx-xxxx"
                      inputMode="tel"
                      pattern="^08\\d-\\d{3}-\\d{4}$"
                      maxLength={12}
                    />
                  </div>
                  <div className="col-md-2">
                    <label style={labelStyle}>Username</label>
                    <input
                      type="text"
                      className="form-control"
                      value={member.member_username || ""}
                      onChange={(e) =>
                        handleMemberChange(index, "member_username", e.target.value)
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div className="col-md-2">
                    <label style={labelStyle}>Password</label>
                    <div className="input-group">
                      <input
                        type={showMemberPasswords[index] ? "text" : "password"}
                        className="form-control"
                        value={member.member_password || ""}
                        onChange={(e) =>
                          handleMemberChange(index, "member_password", e.target.value)
                        }
                        style={inputStyle}
                      />
                      <button
                        type="button"
                        className="btn text-white"
                        onClick={() => toggleShowMemberPassword(index)}
                        style={{
                          backgroundColor: THEME_COLOR,
                          borderRadius: "0 8px 8px 0",
                          padding: "0 12px",
                          border: "none",
                        }}
                      >
                        <i className={showMemberPasswords[index] ? "fas fa-eye-slash" : "fas fa-eye"} />
                      </button>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <label style={labelStyle}>PIN</label>
                    <input
                      type="text"
                      className="form-control"
                      value={member.member_pin || ""}
                      onChange={(e) =>
                        handleMemberChange(index, "member_pin", e.target.value)
                      }
                      style={inputStyle}
                      maxLength={20}
                    />
                  </div>
                  <div className="col-md-1">
                    <label style={labelStyle}>&nbsp;</label>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger w-100"
                      onClick={() => handleRemoveMember(index)}
                      style={{ marginTop: "6px" }}
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {(!formData.team_members || formData.team_members.length === 0) && (
            <div className="col-12">
              <p style={{ color: "#6b7280", fontStyle: "italic" }}>
                ยังไม่มีลูกทีม คลิกปุ่ม "เพิ่มลูกทีม" เพื่อเพิ่ม
              </p>
            </div>
          )}
        </div>
      </div>
    </InitialDataFormLayout>
  );
};

export default TeamForm;

